import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, GraduationCap, Mail, Lock, AlertTriangle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login, isLoading, isSuperAdmin, apisReady, isInitialized, retryInitialization } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Check if APIs are ready before attempting login
    if (!apisReady) {
      setError('Authentication system is not ready. Please wait a moment or refresh the page.');
      return;
    }

    try {
      console.log('Attempting login with credentials:', { email });

      // Pass credentials object to login function
      await login({ email, password });

      console.log('Login successful, checking user role...');

      // Small delay to ensure the auth context is updated
      setTimeout(() => {
        // Navigate based on user role
        if (email === 'admin@teachai.com' || email === 'admin@ezsite.ai' || email === 'support@ezsite.ai') {
          console.log('Superadmin detected, navigating to superadmin dashboard');
          navigate('/superadmin');
        } else {
          console.log('Regular user, navigating to dashboard');
          navigate('/dashboard');
        }
      }, 100);

    } catch (err: any) {
      console.error('Login error:', err);
      // Show the actual error message from the API
      setError(err.message || 'Invalid credentials. Please try again.');
    }
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  const handleRetryConnection = () => {
    setError('');
    retryInitialization();
  };

  // Show loading spinner while authentication is initializing
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" data-id="8mspsaguu" data-path="src/components/auth/LoginForm.tsx">
        <div className="text-center" data-id="0ckwyxufw" data-path="src/components/auth/LoginForm.tsx">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4" data-id="tk50gubsl" data-path="src/components/auth/LoginForm.tsx"></div>
          <p className="text-gray-600 mb-4" data-id="tyblf1xfg" data-path="src/components/auth/LoginForm.tsx">Initializing authentication system...</p>
          <Button
            variant="outline"
            onClick={handleRefreshPage}
            className="text-sm" data-id="us6zuoqp9" data-path="src/components/auth/LoginForm.tsx">

            <RefreshCw className="h-4 w-4 mr-2" data-id="ofs2ixbtw" data-path="src/components/auth/LoginForm.tsx" />
            Refresh Page
          </Button>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4" data-id="2oh8i56t4" data-path="src/components/auth/LoginForm.tsx">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md" data-id="9vuhqunrv" data-path="src/components/auth/LoginForm.tsx">

        <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm" data-id="fwe1woa7b" data-path="src/components/auth/LoginForm.tsx">
          <CardHeader className="text-center pb-6" data-id="1m4vtpw1x" data-path="src/components/auth/LoginForm.tsx">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="flex justify-center mb-4" data-id="3bl73bk8j" data-path="src/components/auth/LoginForm.tsx">

              <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full" data-id="jv13al3w9" data-path="src/components/auth/LoginForm.tsx">
                <GraduationCap className="h-8 w-8 text-white" data-id="tncl156i1" data-path="src/components/auth/LoginForm.tsx" />
              </div>
            </motion.div>
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" data-id="w7nyv1s7e" data-path="src/components/auth/LoginForm.tsx">
              Soyous AI
            </CardTitle>
            <CardDescription className="text-gray-600" data-id="qj5yi1w7u" data-path="src/components/auth/LoginForm.tsx">
              Sign in to access your educational AI tools
            </CardDescription>
          </CardHeader>
          
          <form onSubmit={handleSubmit} data-id="508hcmtjx" data-path="src/components/auth/LoginForm.tsx">
            <CardContent className="space-y-4" data-id="1vc1jetoy" data-path="src/components/auth/LoginForm.tsx">
              {/* Connection Status Indicator */}
              <div className="flex items-center justify-center mb-4" data-id="j5ssmoqup" data-path="src/components/auth/LoginForm.tsx">
                <div className="flex items-center text-sm" data-id="oll74himn" data-path="src/components/auth/LoginForm.tsx">
                  {apisReady ?
                  <>
                      <Wifi className="h-4 w-4 text-green-500 mr-2" data-id="kjfkl9ikr" data-path="src/components/auth/LoginForm.tsx" />
                      <span className="text-green-600" data-id="sdsgkl43n" data-path="src/components/auth/LoginForm.tsx">Connected</span>
                    </> :

                  <>
                      <WifiOff className="h-4 w-4 text-orange-500 mr-2" data-id="dq6j7d7pv" data-path="src/components/auth/LoginForm.tsx" />
                      <span className="text-orange-600" data-id="gxzgsmqmo" data-path="src/components/auth/LoginForm.tsx">Connecting...</span>
                    </>
                  }
                </div>
              </div>

              {!apisReady &&
              <Alert className="bg-orange-50 border-orange-200" data-id="46ocuxspd" data-path="src/components/auth/LoginForm.tsx">
                  <AlertTriangle className="h-4 w-4 text-orange-600" data-id="9973j5kkl" data-path="src/components/auth/LoginForm.tsx" />
                  <AlertDescription className="text-orange-800" data-id="jiqiksvvh" data-path="src/components/auth/LoginForm.tsx">
                    <div className="flex flex-col gap-2" data-id="8ar430abr" data-path="src/components/auth/LoginForm.tsx">
                      <span data-id="hwsxotf7c" data-path="src/components/auth/LoginForm.tsx">Authentication system is connecting...</span>
                      <div className="flex gap-2" data-id="1w40m8o24" data-path="src/components/auth/LoginForm.tsx">
                        <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRetryConnection}
                        className="text-xs h-7" data-id="ihw9qy0mk" data-path="src/components/auth/LoginForm.tsx">

                          <RefreshCw className="h-3 w-3 mr-1" data-id="9mppk5y7o" data-path="src/components/auth/LoginForm.tsx" />
                          Retry
                        </Button>
                        <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRefreshPage}
                        className="text-xs h-7" data-id="8apuubyke" data-path="src/components/auth/LoginForm.tsx">

                          <RefreshCw className="h-3 w-3 mr-1" data-id="7mudejlag" data-path="src/components/auth/LoginForm.tsx" />
                          Refresh Page
                        </Button>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              }

              {error &&
              <Alert variant="destructive" data-id="b2ezmo0hr" data-path="src/components/auth/LoginForm.tsx">
                  <AlertDescription data-id="ce3n3rcpb" data-path="src/components/auth/LoginForm.tsx">{error}</AlertDescription>
                </Alert>
              }
              
              <div className="space-y-2" data-id="vbuys57h2" data-path="src/components/auth/LoginForm.tsx">
                <Label htmlFor="email" className="text-sm font-medium" data-id="4yudy88tn" data-path="src/components/auth/LoginForm.tsx">
                  Email Address
                </Label>
                <div className="relative" data-id="k5ym1qlv9" data-path="src/components/auth/LoginForm.tsx">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" data-id="k2ucfduxt" data-path="src/components/auth/LoginForm.tsx" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="teacher@school.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required data-id="9d3flebnb" data-path="src/components/auth/LoginForm.tsx" />
                </div>
              </div>
              
              <div className="space-y-2" data-id="xust02skt" data-path="src/components/auth/LoginForm.tsx">
                <Label htmlFor="password" className="text-sm font-medium" data-id="ra18rajof" data-path="src/components/auth/LoginForm.tsx">
                  Password
                </Label>
                <div className="relative" data-id="c0c91j3sp" data-path="src/components/auth/LoginForm.tsx">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" data-id="gkxqxps3b" data-path="src/components/auth/LoginForm.tsx" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required data-id="rgu8e5n99" data-path="src/components/auth/LoginForm.tsx" />
                </div>
              </div>
            </CardContent>
            
            <CardFooter className="flex flex-col space-y-4" data-id="dae5v2k81" data-path="src/components/auth/LoginForm.tsx">
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white"
                disabled={isLoading || !apisReady} data-id="mmp444w9z" data-path="src/components/auth/LoginForm.tsx">

                {isLoading ?
                <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="3sme2z5q6" data-path="src/components/auth/LoginForm.tsx" />
                    Signing in...
                  </> :

                'Sign In'
                }
              </Button>
              
              <div className="text-center text-sm" data-id="9ko70jz9c" data-path="src/components/auth/LoginForm.tsx">
                <span className="text-gray-600" data-id="k6x34e0fb" data-path="src/components/auth/LoginForm.tsx">Don't have an account? </span>
                <Button
                  type="button"
                  variant="link"
                  onClick={() => navigate('/register')}
                  className="p-0 h-auto font-medium text-blue-600 hover:text-blue-700" data-id="1ml16mv7w" data-path="src/components/auth/LoginForm.tsx">
                  Create one here
                </Button>
              </div>

              <div className="text-center text-sm" data-id="0bsrcnsna" data-path="src/components/auth/LoginForm.tsx">
                <Button
                  type="button"
                  variant="link"
                  onClick={() => navigate('/resetpassword')}
                  className="p-0 h-auto text-gray-500 hover:text-gray-700" data-id="18n598p70" data-path="src/components/auth/LoginForm.tsx">
                  Forgot your password?
                </Button>
              </div>
            </CardFooter>
          </form>
        </Card>
      </motion.div>
    </div>);

};

export default LoginForm;